'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  BookOpen,
  Briefcase,
  Database,
  FileEdit,
  FileText,
  Gauge,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Puzzle,
  Send,
  ShieldCheck,
  Star,
  BookMarked,
  ClipboardCheck,
} from 'lucide-react'

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  label: string
  href: string
  icon: React.ElementType
  external?: boolean
  subItem?: boolean
}

const featureItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Job Tracker', href: '/tracker', icon: Briefcase },
  { label: 'ATS Test', href: '/ats-checker', icon: ShieldCheck },
  { label: 'Statement Checker', href: '/supporting-statement-checker', icon: ClipboardCheck },
  { label: 'LinkedIn Rewrite', href: '/linkedin-rewrite', icon: LinkedInIcon },
  { label: 'Outreach Generator', href: '/networking-email', icon: Send },
  { label: 'Interview Prep', href: '/interview-prep', icon: MessageSquare },
  { label: 'Career Bank', href: '/career-bank', icon: Database },
  { label: 'Saved Answers', href: '/saved-answers', icon: BookMarked },
  { label: 'Usage', href: '/usage', icon: Gauge },
]

const cvDocItems: NavItem[] = [
  { label: 'Your CVs', href: '/my-cvs', icon: FileText },
]

const clDocItems: NavItem[] = [
  { label: 'Your Cover Letters', href: '/my-cover-letters', icon: FileEdit },
]

const resourceItems: NavItem[] = [
  { label: 'Job Search Copilot', href: '/features/job-search-copilot', icon: Puzzle },
  { label: 'Blogs', href: '/blog', icon: BookOpen },
  { label: 'Features', href: '/features', icon: Star },
]

const contactItem: NavItem = { label: 'Contact', href: '/contact', icon: Mail }

function NavLink({ item, pathname, onClick }: { item: NavItem; pathname: string; onClick?: () => void }) {
  const isActive = pathname === item.href || (item.href !== '/dashboard' && pathname.startsWith(item.href))
  const Icon = item.icon

  return (
    <Link
      href={item.href}
      onClick={onClick}
      target={item.external ? '_blank' : undefined}
      rel={item.external ? 'noopener noreferrer' : undefined}
      className={`flex items-center gap-3 pl-[9px] pr-3 py-2 rounded-md border-l-[3px] text-sm font-medium transition-all group ${
        isActive
          ? 'border-white bg-white/15 text-white font-semibold'
          : 'border-transparent text-primary-100 hover:bg-primary-600 hover:text-white'
      }`}
    >
      <Icon
        className={`w-4 h-4 shrink-0 transition-colors ${
          isActive ? 'text-white' : 'text-primary-200 group-hover:text-primary-100'
        }`}
      />
      {item.label}
    </Link>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-primary-200">
      {children}
    </p>
  )
}


function SidebarContent({ pathname, onLinkClick, inDrawer = false }: { pathname: string; onLinkClick?: () => void; inDrawer?: boolean }) {
  return (
    <div className="flex flex-col h-full">
      {/* Scrolls rather than clips: the full list needs ~650px, more than a
          768px-tall laptop or most phone viewports leave once the header and
          gaps are taken out, and clipped links were simply unreachable. */}
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 flex flex-col [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.35)_transparent]">
        {/* Features — no section label; this is the first/primary group so
            it doesn't need a heading the way Documents/Resources below it do. */}
        <div className="space-y-0.5">
          {featureItems.map((item) => (
            <NavLink key={item.href} item={item} pathname={pathname} onClick={onLinkClick} />
          ))}
        </div>

        {/* Documents (previously labelled "Resources") */}
        <div className="mt-6">
          <SectionLabel>Documents</SectionLabel>
          <div className="space-y-0.5">
            {cvDocItems.map((item) => (
              <NavLink key={item.href} item={item} pathname={pathname} onClick={onLinkClick} />
            ))}
            {clDocItems.map((item) => (
              <NavLink key={item.href} item={item} pathname={pathname} onClick={onLinkClick} />
            ))}
          </div>
        </div>

        {/* Resources (previously labelled "Explore") — mt-auto (not mt-6)
            deliberately: `nav` is now a flex column so this pins the section
            to the true bottom of the sidebar panel, not just last-in-order
            with empty space left dangling beneath it. */}
        <div className="mt-auto pt-6">
          <SectionLabel>Resources</SectionLabel>
          <div className="space-y-0.5">
            {resourceItems.map((item) => (
              <NavLink key={item.href} item={item} pathname={pathname} onClick={onLinkClick} />
            ))}
            {/* AppHeader drops its own Contact link below 390px to make room
                for the upgrade button; this keeps it reachable there. */}
            {inDrawer && (
              <div className="min-[390px]:hidden">
                <NavLink item={contactItem} pathname={pathname} onClick={onLinkClick} />
              </div>
            )}
          </div>
        </div>
      </nav>
    </div>
  )
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const pathname = usePathname()

  return (
    <>
      {/* Desktop sidebar — a floating "island" panel (gap on every side,
          rounded corners, shadow), fixed-positioned with explicit top/left/
          bottom offsets rather than h-full so the gap survives independent
          of viewport height. top-[108px] = the header's own floating
          position (16px gap + 76px header height) plus another 16px gap
          before the sidebar starts. That 76px is AppHeader's 44px profile
          button plus its 32px of vertical padding — change either and this
          offset (and the two mobile ones below) must move with it. Spacer
          width (240px) is the sidebar's
          own 224px (w-56) plus its 16px left gap, so it still reserves the
          right amount of space in the flex row. Mobile keeps the original
          edge-to-edge overlay below — floating chrome is a desktop-only
          treatment, since gaps cost real space on a phone screen. */}
      <div className="hidden lg:block w-[240px] shrink-0 flex-none" />
      <aside className="hidden lg:flex flex-col w-56 bg-[linear-gradient(160deg,#0284c7_0%,#0369a1_40%,#0c4a6e_100%)] border border-primary-700 overflow-hidden z-30 fixed top-[108px] left-4 bottom-4 rounded-2xl shadow-lg">
        <SidebarContent pathname={pathname} />
      </aside>

      {/* Mobile overlay backdrop — sits below the sticky header */}
      {isOpen && (
        <div
          className="fixed inset-x-0 bottom-0 top-[69px] sm:top-[77px] bg-black/40 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar — slides in from left, starts below the sticky header
          (69px tall below sm, 77px from sm up: the same 44px profile button,
          plus 24px/32px padding and the header's 1px bottom border). */}
      <aside
        className={`fixed top-[69px] sm:top-[77px] left-0 bottom-0 w-64 bg-[linear-gradient(160deg,#0284c7_0%,#0369a1_40%,#0c4a6e_100%)] border-r border-primary-700 shadow-xl z-50 flex flex-col transform transition-transform duration-200 ease-in-out lg:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex-1 min-h-0 overflow-hidden">
          <SidebarContent pathname={pathname} onLinkClick={onClose} inDrawer />
        </div>
      </aside>
    </>
  )
}
